import { useAuth } from '../auth/useAuth'

/* ─── Role-based section gating (MS-741 AC3 / R-ROLE-1) ───
   The single source of role truth for every Studio surface and tab. Ported
   verbatim from the prototype (agent-studio.html:6698-6717) so drift from the
   design source is a diff, not a judgement call.

   IMPORTANT — this is a UX gate, not a security boundary. It exists so a role
   without access is not sent to a page that will only 403 on its first fetch.
   The real enforcement is server-side: every endpoint these surfaces call runs
   `require_global_permission`/`require_agent_permission` at the correct level
   (see src/studio/auth/access.py). Do not weaken a backend check on the
   strength of this module. */

export type StudioRoleName = 'Admin' | 'Editor'

export type SectionKey =
  | 'home'
  | 'agents'
  | 'mcp'
  | 'global'
  | 'marketplace'
  | 'workflows'
  | 'audit'
  | 'analytics'
  | 'mcp-analytics'

export type AgentTabKey = 'dna' | 'skills' | 'connections' | 'knowledge' | 'guardrails' | 'access'

export type GlobalTabKey =
  | 'skills'
  | 'knowledge'
  | 'connections'
  | 'guardrails'
  | 'access'

// agent-studio.html:6698-6717
export const ROLE_ACCESS: Record<SectionKey, StudioRoleName[]> = {
  home: ['Admin', 'Editor'],
  agents: ['Admin', 'Editor'],
  mcp: ['Admin'],
  global: ['Admin', 'Editor'],
  marketplace: ['Admin', 'Editor'],
  workflows: ['Admin', 'Editor'],
  audit: ['Admin'],
  analytics: ['Admin', 'Editor'],
  'mcp-analytics': ['Admin'],
}

export const AGENT_TAB_ACCESS: Record<AgentTabKey, StudioRoleName[]> = {
  dna: ['Admin', 'Editor'],
  skills: ['Admin', 'Editor'],
  connections: ['Admin'],
  knowledge: ['Admin', 'Editor'],
  guardrails: ['Admin'],
  access: ['Admin'],
}

// The prototype's `models` tab is deliberately absent here (agent-studio.html:6717-6718)
// so its existing feature-flag `hidden` state is left alone — an id absent from this
// map is not role-gated (see roleAllowed below).
export const GLOBAL_TAB_ACCESS: Record<GlobalTabKey, StudioRoleName[]> = {
  skills: ['Admin', 'Editor'],
  knowledge: ['Admin', 'Editor'],
  connections: ['Admin'],
  guardrails: ['Admin'],
  access: ['Admin'],
}

/** Returns `true` when `id` is absent from `map` (matching the prototype's
 * `roleAllowed`, agent-studio.html:6723-6726) — an unlisted id is not role-gated. */
function roleAllowed<K extends string>(
  map: Record<K, StudioRoleName[]>,
  id: K,
  role: StudioRoleName | null
): boolean {
  const allowed = map[id]
  if (!allowed) return true
  if (!role) return false
  return allowed.includes(role)
}

/** The effective Studio role for the current session (`SessionResponse.studioRole`). */
export function useStudioRoleName(): StudioRoleName | null {
  const { studioRole } = useAuth()
  if (studioRole === 'Admin' || studioRole === 'Editor') return studioRole
  return null
}

export interface SectionAccess {
  canOpen(section: SectionKey): boolean
  canOpenAgentTab(id: AgentTabKey): boolean
  canOpenGlobalTab(id: GlobalTabKey): boolean
  /** False while the role is still unknown — a profile cached before `studioRole`
   * existed (see AuthProvider's `studioRolePending`). The `canOpen*` predicates
   * stay `false` then, so nothing fetches an endpoint that would 403; but a guard
   * must HOLD rather than redirect, because a deny decided on an unresolved role
   * is wrong and its redirect is not undone once the real role arrives. */
  isRoleResolved: boolean
}

export function useSectionAccess(): SectionAccess {
  const role = useStudioRoleName()
  const { studioRolePending } = useAuth()
  return {
    canOpen: (section) => roleAllowed(ROLE_ACCESS, section, role),
    canOpenAgentTab: (id) => roleAllowed(AGENT_TAB_ACCESS, id, role),
    canOpenGlobalTab: (id) => roleAllowed(GLOBAL_TAB_ACCESS, id, role),
    isRoleResolved: !studioRolePending,
  }
}
